import DOMPurify from "dompurify";

const BLOG_SCHEMA_VERSION = 2;

export const getBlogSchemaVersion = () => BLOG_SCHEMA_VERSION;

export const legacyMarkdownToHtml = (content = "") => {
  if (!content) {
    return "";
  }

  const lines = content.split(/\r?\n/);
  const blocks = [];
  let paragraphBuffer = [];
  let listBuffer = [];

  const flushParagraph = () => {
    if (paragraphBuffer.length > 0) {
      blocks.push(`<p>${applyInlineFormatting(paragraphBuffer.join("<br />"))}</p>`);
      paragraphBuffer = [];
    }
  };

  const flushList = () => {
    if (listBuffer.length > 0) {
      const listItems = listBuffer
        .map((item) => `<li>${applyInlineFormatting(item)}</li>`)
        .join("");
      blocks.push(`<ul>${listItems}</ul>`);
      listBuffer = [];
    }
  };

  lines.forEach((line) => {
    const trimmed = line.trim();

    if (!trimmed) {
      flushParagraph();
      flushList();
      return;
    }

    if (trimmed.startsWith("### ")) {
      flushParagraph();
      flushList();
      blocks.push(`<h3>${applyInlineFormatting(trimmed.slice(4))}</h3>`);
      return;
    }

    if (trimmed.startsWith("## ")) {
      flushParagraph();
      flushList();
      blocks.push(`<h2>${applyInlineFormatting(trimmed.slice(3))}</h2>`);
      return;
    }

    if (trimmed.startsWith("# ")) {
      flushParagraph();
      flushList();
      blocks.push(`<h1>${applyInlineFormatting(trimmed.slice(2))}</h1>`);
      return;
    }

    if (trimmed.startsWith("- ")) {
      flushParagraph();
      listBuffer.push(trimmed.slice(2));
      return;
    }

    flushList();
    paragraphBuffer.push(trimmed);
  });

  flushParagraph();
  flushList();

  return blocks.join("");
};

const applyInlineFormatting = (value) =>
  value
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.*?)\*/g, "<em>$1</em>");

export const sanitizeBlogHtml = (html = "") =>
  DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
  });

export const htmlToPlainText = (html = "") => {
  if (typeof window !== "undefined" && window.document) {
    const container = window.document.createElement("div");
    container.innerHTML = html;
    return (container.textContent || container.innerText || "").replace(/\s+/g, " ").trim();
  }

  return html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
};

export const buildExcerptFromHtml = (html = "", maxLength = 170) => {
  const plainText = htmlToPlainText(html);

  if (plainText.length <= maxLength) {
    return plainText;
  }

  return `${plainText.slice(0, maxLength).trim()}...`;
};

export const calculateReadTime = (html = "") => {
  const wordCount = htmlToPlainText(html)
    .split(/\s+/)
    .filter(Boolean).length;

  const minutes = Math.max(1, Math.ceil(wordCount / 200));
  return `${minutes} min read`;
};

export const normalizeBlogPost = (post) => {
  const rawContent = post.content || "";
  const contentFormat = post.contentFormat || (rawContent.trim().startsWith("<") ? "html" : "markdown");
  const contentHtml =
    contentFormat === "html"
      ? sanitizeBlogHtml(rawContent)
      : sanitizeBlogHtml(legacyMarkdownToHtml(rawContent));

  return {
    ...post,
    content: contentHtml,
    contentFormat: "html",
    excerpt: post.excerpt || buildExcerptFromHtml(contentHtml),
    readTime: post.readTime || calculateReadTime(contentHtml),
    tags: Array.isArray(post.tags) ? post.tags : [],
    schemaVersion: BLOG_SCHEMA_VERSION,
  };
};
